package com.elan.edubridge.shared.data.repository

import com.elan.edubridge.shared.data.datasource.AccountDataSource
import com.elan.edubridge.shared.domain.model.AcademyMembership
import com.elan.edubridge.shared.domain.model.AgeGroup
import com.elan.edubridge.shared.domain.model.AuthResult
import com.elan.edubridge.shared.domain.model.BirthDate
import com.elan.edubridge.shared.domain.model.AccountDeletionState
import com.elan.edubridge.shared.domain.model.ProfileUpdateResult
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.UserAccount
import com.elan.edubridge.shared.domain.repository.AccountRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * 세션 상태 보유자. UI는 [session]만 구독하면 된다.
 */
class AccountRepositoryImpl(
    private val dataSource: AccountDataSource,
    private val scope: CoroutineScope,
) : AccountRepository {
    private val mutableSession = MutableStateFlow(SessionState())

    override val session: StateFlow<SessionState> = mutableSession.asStateFlow()

    override fun currentSession(): SessionState = mutableSession.value

    override suspend fun restoreSession(): SessionState {
        // 영속 상태 복원이 끝나기 전의 currentAccount()는 항상 null이다.
        dataSource.awaitAuthReady()
        return publish(dataSource.currentAccount())
    }

    override suspend fun login(email: String, password: String): AuthResult {
        val result = dataSource.login(email.trim(), password)
        result.account?.let { publish(it) }
        return result
    }

    override suspend fun register(
        email: String,
        password: String,
        name: String,
        birthDate: String,
        ageGroup: AgeGroup,
    ): AuthResult {
        if (name.trim().isBlank()) {
            return AuthResult(errorMessage = "이름을 입력해 주세요.")
        }
        val normalizedBirthDate = BirthDate.normalize(birthDate)
            ?: return AuthResult(errorMessage = "생년월일을 YYYY-MM-DD 형식으로 올바르게 입력해 주세요.")
        val result = dataSource.register(email.trim(), password, name.trim(), normalizedBirthDate, ageGroup)
        result.account?.let { publish(it) }
        return result
    }

    override suspend fun signInWithGoogle(
        idToken: String?,
        registrationName: String?,
        birthDate: String?,
    ): AuthResult {
        val normalizedName = registrationName?.trim()
        val normalizedBirthDate = birthDate?.let(BirthDate::normalize)
        if ((normalizedName == null) != (birthDate == null) ||
            (normalizedName != null && (normalizedName.isBlank() || normalizedBirthDate == null))
        ) {
            return AuthResult(errorMessage = "Google 회원가입에는 이름과 올바른 생년월일이 필요해요.")
        }
        val result = dataSource.signInWithGoogle(idToken, normalizedName, normalizedBirthDate)
        result.account?.let { publish(it) }
        return result
    }

    override suspend fun logout() {
        dataSource.logout()
        mutableSession.value = SessionState()
    }

    override suspend fun updateMyProfile(name: String): ProfileUpdateResult {
        val result = dataSource.updateMyProfile(name.trim())
        val updatedName = result.name
        if (result.isSuccess && updatedName != null) {
            val current = mutableSession.value
            current.account?.let { account ->
                mutableSession.value = current.copy(account = account.copy(name = updatedName))
            }
        }
        return result
    }

    override suspend fun accountDeletionStatus(): AccountDeletionState = dataSource.accountDeletionStatus()

    override suspend fun requestAccountDeletion(reason: String?): AccountDeletionState =
        dataSource.requestAccountDeletion(reason)

    override suspend fun cancelAccountDeletion(): AccountDeletionState =
        dataSource.cancelAccountDeletion()

    override fun selectAcademy(academyId: String?) {
        mutableSession.value = mutableSession.value.copy(activeAcademyId = academyId)
    }

    override suspend fun refreshMemberships(): SessionState {
        val current = mutableSession.value
        val account = current.account ?: return publish(null)
        val memberships = dataSource.memberships(account.id)
        val next = current.copy(
            memberships = memberships,
            activeAcademyId = resolveActiveAcademyId(memberships, current.activeAcademyId),
        )
        mutableSession.value = next
        return next
    }

    override suspend fun forceRefreshToken(): SessionState {
        dataSource.forceRefreshToken()
        // 갱신된 claim이 반영된 계정을 다시 읽어야 globalRoles 변화가 세션에 들어온다.
        val account = dataSource.currentAccount() ?: return publish(null)
        mutableSession.value = mutableSession.value.copy(account = account)
        return refreshMemberships()
    }

    /**
     * 인증된 계정을 세션으로 승격한다. 계정이 null이면 미인증 상태로 되돌린다.
     * 로그인 실패는 [login]이 걸러내므로 여기까지 오지 않는다.
     */
    private suspend fun publish(account: UserAccount?): SessionState {
        val next = if (account == null) {
            SessionState()
        } else {
            val memberships = dataSource.memberships(account.id)
            SessionState(
                account = account,
                memberships = memberships,
                activeAcademyId = resolveActiveAcademyId(memberships, activeAcademyId = null),
            )
        }
        mutableSession.value = next
        return next
    }

    private fun resolveActiveAcademyId(
        memberships: List<AcademyMembership>,
        activeAcademyId: String?,
    ): String? {
        val usable = memberships.filter { it.isUsable }
        return usable.firstOrNull { it.academyId == activeAcademyId }?.academyId
            ?: usable.firstOrNull()?.academyId
    }
}
